import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CrudApiOptions, GenResp } from "@template-ui/services/crud-api.service";
import { AppBaseService } from "@app/shared/services/app-base.service";
import { catchError, map, Observable, throwError } from "rxjs";
import {
  PlatformAccessAssignmentRevokeRequest,
  PlatformAccessAssignmentsDetail,
  PlatformNavigationNodeCreateRequest,
  PlatformNavigationNodeDetail,
  PlatformNavigationNodeUpdateRequest,
  PlatformPermissionCatalogItem,
  PlatformPermissionGrant,
  PlatformPermissionGrantCreateRequest,
  PlatformRoleCatalogItem,
  PlatformRoleAssignment,
  PlatformRoleAssignmentCreateRequest,
  PlatformUserCreateRequest,
  PlatformUserCreated,
  PlatformUserEffectivePermissions,
  PlatformUserProfileUpdateRequest,
  PlatformScopeTypeCatalogItem,
  PlatformScopeValueCatalogItem,
  PlatformScopeValueCreateRequest,
  PlatformScopeValueUpdateRequest,
  mapPlatformAccessAdminError,
} from "./platform-access-admin.models";

const requestHeaders = new HttpHeaders({
  "Content-Type": "application/json",
  Accept: "application/json, text/plain, */*",
  "skip-error-interceptor": "yes",
  "skip-loading-interceptor": "yes",
});

const platformAccessAdminApiOptions: Partial<CrudApiOptions<"body", "json">> = {
  retries: 0,
  feedbackOptions: {
    successMode: "none",
    errorMode: "none",
  },
  getOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  postOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
  putOptions: {
    responseType: "json",
    headers: requestHeaders,
    withCredentials: true,
  },
};

@Injectable({ providedIn: "root" })
export class PlatformAccessAdminService extends AppBaseService {
  getAssignments(userId: number): Observable<PlatformAccessAssignmentsDetail> {
    return this.crudService.getbyUrl<PlatformAccessAssignmentsDetail>(
      this.apis.core.manual.getPlatformAccessAssignments(userId),
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  /**
   * Reads the permissions the runtime evaluation grants a user, each with every role or
   * direct-grant source. Failures arrive already mapped through mapPlatformAccessAdminError
   * so the view can show the problem code and correlation id beside its Retry action.
   */
  getUserEffectivePermissions(userId: number): Observable<PlatformUserEffectivePermissions> {
    return this.crudService.getbyUrl<PlatformUserEffectivePermissions>(
      this.apis.core.manual.getPlatformAccessUserEffectivePermissions(userId),
      platformAccessAdminApiOptions,
    ).pipe(
      map((response) => unwrapResponse(response)),
      catchError((error) => throwError(() => mapPlatformAccessAdminError(error))),
    );
  }

  getUsersODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessUsersOData();
  }

  /**
   * Creates a user with the role and the password they start with. Failures arrive already
   * mapped through mapPlatformAccessAdminError, so a validation problem keeps its field keys
   * (fieldMessages) and its correlation id for the new user form to display.
   */
  createUser(request: PlatformUserCreateRequest): Observable<PlatformUserCreated> {
    return this.crudService.postByUrl<PlatformUserCreated>(
      this.apis.core.manual.postPlatformAccessUsers(),
      request,
      platformAccessAdminApiOptions,
    ).pipe(
      map((response) => unwrapResponse(response)),
      catchError((error) => throwError(() => mapPlatformAccessAdminError(error))),
    );
  }

  updateUserProfile(
    userId: number,
    request: PlatformUserProfileUpdateRequest,
  ): Observable<PlatformAccessAssignmentsDetail> {
    return this.crudService.putByUrl<PlatformAccessAssignmentsDetail>(
      this.apis.core.manual.putPlatformAccessUser(userId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  getRolesODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessRolesOData();
  }

  getPermissionsODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessPermissionsOData();
  }

  getScopeTypesODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessScopeTypesOData();
  }

  getScopeValuesODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessScopeValuesOData();
  }

  listRoles(): Observable<PlatformRoleCatalogItem[]> {
    return this.crudService.getbyUrl<PlatformRoleCatalogItem[]>(
      `${this.apis.core.manual.getPlatformAccessRolesOData()}?$filter=IsActive eq true&$orderby=Name,Code`,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapCollectionResponse(response)));
  }

  listPermissions(): Observable<PlatformPermissionCatalogItem[]> {
    return this.crudService.getbyUrl<PlatformPermissionCatalogItem[]>(
      `${this.apis.core.manual.getPlatformAccessPermissionsOData()}?$filter=IsActive eq true&$orderby=Name,Code`,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapCollectionResponse(response)));
  }

  listScopeTypes(): Observable<PlatformScopeTypeCatalogItem[]> {
    return this.crudService.getbyUrl<PlatformScopeTypeCatalogItem[]>(
      `${this.apis.core.manual.getPlatformAccessScopeTypesOData()}?$filter=IsActive eq true&$orderby=SortOrder,Name,Code`,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapCollectionResponse(response)));
  }

  listScopeValues(): Observable<PlatformScopeValueCatalogItem[]> {
    return this.crudService.getbyUrl<PlatformScopeValueCatalogItem[]>(
      `${this.apis.core.manual.getPlatformAccessScopeValuesOData()}?$filter=IsActive eq true&$orderby=ScopeTypeName,DisplayName,ScopeKey`,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapCollectionResponse(response)));
  }

  createScopeValue(request: PlatformScopeValueCreateRequest): Observable<PlatformScopeValueCatalogItem> {
    return this.crudService.postByUrl<PlatformScopeValueCatalogItem>(
      this.apis.core.manual.postPlatformAccessScopeValues(),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  updateScopeValue(
    scopeValueId: number,
    request: PlatformScopeValueUpdateRequest,
  ): Observable<PlatformScopeValueCatalogItem> {
    return this.crudService.putByUrl<PlatformScopeValueCatalogItem>(
      this.apis.core.manual.putPlatformAccessScopeValue(scopeValueId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  assignRole(userId: number, request: PlatformRoleAssignmentCreateRequest): Observable<PlatformRoleAssignment> {
    return this.crudService.postByUrl<PlatformRoleAssignment>(
      this.apis.core.manual.postPlatformAccessRoleAssignments(userId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  revokeRole(
    userId: number,
    assignmentId: number,
    request: PlatformAccessAssignmentRevokeRequest,
  ): Observable<PlatformRoleAssignment> {
    return this.crudService.postByUrl<PlatformRoleAssignment>(
      this.apis.core.manual.postPlatformAccessRoleAssignmentRevoke(userId, assignmentId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  grantPermission(
    userId: number,
    request: PlatformPermissionGrantCreateRequest,
  ): Observable<PlatformPermissionGrant> {
    return this.crudService.postByUrl<PlatformPermissionGrant>(
      this.apis.core.manual.postPlatformAccessPermissionGrants(userId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  revokePermission(
    userId: number,
    grantId: number,
    request: PlatformAccessAssignmentRevokeRequest,
  ): Observable<PlatformPermissionGrant> {
    return this.crudService.postByUrl<PlatformPermissionGrant>(
      this.apis.core.manual.postPlatformAccessPermissionGrantRevoke(userId, grantId),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  listNavigationNodes(): Observable<PlatformNavigationNodeDetail[]> {
    return this.crudService.getbyUrl<PlatformNavigationNodeDetail[]>(
      this.apis.core.manual.getPlatformAccessNavigation(),
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  getNavigationODataEndpoint(): string {
    return this.apis.core.manual.getPlatformAccessNavigationOData();
  }

  getNavigationNode(nodeCode: string): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.getbyUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.getPlatformAccessNavigationNode(nodeCode),
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  createNavigationNode(request: PlatformNavigationNodeCreateRequest): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.postByUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.postPlatformAccessNavigation(),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  updateNavigationNode(
    nodeCode: string,
    request: PlatformNavigationNodeUpdateRequest,
  ): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.putByUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.putPlatformAccessNavigationNode(nodeCode),
      request,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  publishNavigationNode(nodeCode: string): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.postByUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.postPlatformAccessNavigationPublish(nodeCode),
      null,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  retireNavigationNode(nodeCode: string): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.postByUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.postPlatformAccessNavigationRetire(nodeCode),
      null,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }

  rollbackNavigationNode(nodeCode: string): Observable<PlatformNavigationNodeDetail> {
    return this.crudService.postByUrl<PlatformNavigationNodeDetail>(
      this.apis.core.manual.postPlatformAccessNavigationRollback(nodeCode),
      null,
      platformAccessAdminApiOptions,
    ).pipe(map((response) => unwrapResponse(response)));
  }
}

export { mapPlatformAccessAdminError };

function unwrapResponse<T>(response: GenResp<T> | T | undefined | null): T {
  if (response && typeof response === "object" && "data" in response) {
    return (response as GenResp<T>).data;
  }

  return response as T;
}

function unwrapCollectionResponse<T>(response: GenResp<T[]> | T[] | { value?: T[] } | undefined | null): T[] {
  const unwrapped = unwrapResponse(response as GenResp<unknown> | unknown | undefined | null);
  if (Array.isArray(unwrapped)) {
    return unwrapped as T[];
  }

  const maybeODataResponse = unwrapped as { value?: unknown } | null | undefined;
  if (maybeODataResponse && Array.isArray(maybeODataResponse.value)) {
    return maybeODataResponse.value as T[];
  }

  return [];
}
